/**
 * normalizeXtsConfig — pre-render pass.
 *
 * Fail-fast validation + AT2050 half-clothoid merging:
 *   AT2050_0500 + AT2050_0501 (gap=0)        → AT2050_0500_180
 *   ATH2050_0500 + ATH2050_0501 (gap=0)      → ATH2050_0500_180
 *
 * Errors thrown via XtsViewerErrorException:
 *   - 'unknown-module-type'
 *   - 'unknown-mover-type'
 *   - 'unmatched-clothoid-half'   (0500 without 0501 or vice versa)
 *
 * Warnings (collected, non-fatal) returned alongside the normalized config:
 *   - mover.partOid not in part-list
 *   - duplicate mover.id within an XPU
 *   - mover.partPositionMm out of range (clamped)
 */

import type {
  ModuleEntry,
  ModuleType3D,
  MoverConfig,
  PartConfig,
  ProcessingUnitConfig,
  XtsConfig,
} from '../types.js';
import { XtsViewerErrorException } from '../types.js';
import { isKnownModuleType } from './ModuleCatalog.js';
import { isKnownMoverType } from './MoverCatalog.js';

export interface NormalizedXtsConfig extends XtsConfig {
  /**
   * Same shape as the input, with all parts' modules pre-merged + validated.
   * Mover positions are clamped, but the original values are preserved in
   * `__warnings` for diagnostics.
   */
  readonly __warnings: NormalizationWarning[];
}

export interface NormalizationWarning {
  code:
    | 'mover-out-of-range'
    | 'mover-orphan'
    | 'mover-duplicate-id'
    | 'mover-missing-id'
    | 'mover-invalid-id'
    | 'empty-part'
    | 'prepared-module'
    | 'duplicate-xpu-object-id'
    | 'missing-xpu-object-id'
    | 'invalid-xpu-object-id'
    | 'duplicate-part-object-id'
    | 'missing-part-object-id'
    | 'invalid-part-object-id'
    | 'duplicate-module-global-number'
    | 'missing-module-global-number'
    | 'invalid-module-global-number';
  message: string;
  details?: unknown;
}

const UINT32_MAX = 0xffffffff;

function isValidUint32(n: unknown): n is number {
  return (
    typeof n === 'number' &&
    Number.isInteger(n) &&
    n >= 0 &&
    n <= UINT32_MAX
  );
}

const HALF_PAIRS: ReadonlyArray<{
  a: ModuleType3D;
  b: ModuleType3D;
  merged: ModuleType3D;
}> = [
  { a: 'AT2050_0500', b: 'AT2050_0501', merged: 'AT2050_0500_180' },
  { a: 'ATH2050_0500', b: 'ATH2050_0501', merged: 'ATH2050_0500_180' },
];

function findHalfPair(t: ModuleType3D) {
  for (const pair of HALF_PAIRS) {
    if (pair.a === t) return pair;
  }
  return undefined;
}

function isHalfModule(t: ModuleType3D): boolean {
  return HALF_PAIRS.some((p) => p.a === t || p.b === t);
}

function mergeHalves(modules: ReadonlyArray<ModuleEntry>): ModuleEntry[] {
  const out: ModuleEntry[] = [];
  for (let i = 0; i < modules.length; i++) {
    const cur = modules[i]!;
    const pair = findHalfPair(cur.moduleType);
    const next = modules[i + 1];
    if (pair && next && next.moduleType === pair.b) {
      // Combine status from both halves — if either half is in
      // warning / error, the merged 180° kehre inherits it.
      const merged: ModuleEntry = { moduleType: pair.merged };
      if (cur.status || next.status) {
        merged.status = {
          warning: !!(cur.status?.warning || next.status?.warning),
          error: !!(cur.status?.error || next.status?.error),
        };
      }
      out.push(merged);
      i += 1; // skip the second half
      continue;
    }
    if (pair && (!next || next.moduleType !== pair.b)) {
      throw new XtsViewerErrorException(
        'unmatched-clothoid-half',
        `${pair.a} at index ${i} is not followed by ${pair.b}.`,
        { index: i, moduleType: pair.a },
      );
    }
    // Lone ".._0501" (second half without a leading .._0500) is also invalid.
    if (
      cur.moduleType === 'AT2050_0501' ||
      cur.moduleType === 'ATH2050_0501'
    ) {
      throw new XtsViewerErrorException(
        'unmatched-clothoid-half',
        `Lone ${cur.moduleType} at index ${i} (must follow its ..._0500 partner).`,
        { index: i, moduleType: cur.moduleType },
      );
    }
    out.push(cur);
  }
  return out;
}

function validatePart(part: PartConfig): NormalizationWarning[] {
  const warnings: NormalizationWarning[] = [];
  if (part.modules.length === 0) {
    warnings.push({
      code: 'empty-part',
      message: `Part #${part.globalNumber} has no modules.`,
      details: { partOid: part.globalNumber },
    });
  }
  for (let i = 0; i < part.modules.length; i++) {
    const m = part.modules[i]!;
    if (!isKnownModuleType(m.moduleType)) {
      throw new XtsViewerErrorException(
        'unknown-module-type',
        `Unknown moduleType "${m.moduleType}" in part #${part.globalNumber} index ${i}`,
        { partOid: part.globalNumber, index: i, moduleType: m.moduleType },
      );
    }
  }
  return warnings;
}

function validateMovers(
  xpu: ProcessingUnitConfig,
  partsByOid: Map<number, PartConfig>,
  partTrackLengths: Map<number, number>,
): { movers: MoverConfig[]; warnings: NormalizationWarning[] } {
  const warnings: NormalizationWarning[] = [];
  const seenIds = new Map<number, number>();
  const out: MoverConfig[] = [];

  for (const m of xpu.movers) {
    const part = partsByOid.get(m.partOid);
    if (!part) {
      warnings.push({
        code: 'mover-orphan',
        message: `Mover ${m.id} references partOid=${m.partOid} which is not in XPU ${xpu.objectId}; mover skipped.`,
        details: { mover: m, xpuObjectId: xpu.objectId },
      });
      continue;
    }
    if (m.id === undefined || m.id === null) {
      warnings.push({
        code: 'mover-missing-id',
        message: `Mover at index=${m.index} in XPU ${xpu.objectId} is missing required field 'id'.`,
        details: { mover: m, xpuObjectId: xpu.objectId },
      });
      continue;
    }
    if (!isValidUint32(m.id)) {
      warnings.push({
        code: 'mover-invalid-id',
        message: `Mover id=${String(m.id)} (XPU ${xpu.objectId}) is invalid (must be a non-negative integer ≤ 2^32-1).`,
        details: { mover: m, xpuObjectId: xpu.objectId },
      });
      continue;
    }
    if (seenIds.has(m.id)) {
      warnings.push({
        code: 'mover-duplicate-id',
        message: `Duplicate mover.id=${m.id} in XPU ${xpu.objectId}; later definition wins.`,
        details: { id: m.id, xpuObjectId: xpu.objectId },
      });
      // Replace previous occurrence — last wins.
      const idx = seenIds.get(m.id)!;
      out[idx] = clampMover(m, partTrackLengths.get(m.partOid) ?? 0, warnings);
      continue;
    }
    const trackLength = partTrackLengths.get(m.partOid) ?? 0;
    out.push(clampMover(m, trackLength, warnings));
    seenIds.set(m.id, out.length - 1);
  }
  return { movers: out, warnings };
}

function clampMover(
  mover: MoverConfig,
  trackLengthMm: number,
  warnings: NormalizationWarning[],
): MoverConfig {
  let pos = mover.partPositionMm;
  if (!Number.isFinite(pos)) pos = 0;
  if (pos < 0) {
    warnings.push({
      code: 'mover-out-of-range',
      message: `Mover ${mover.id} partPositionMm=${pos} clamped to 0.`,
      details: { mover, original: mover.partPositionMm },
    });
    pos = 0;
  } else if (pos > trackLengthMm) {
    warnings.push({
      code: 'mover-out-of-range',
      message: `Mover ${mover.id} partPositionMm=${pos} clamped to trackLength=${trackLengthMm}.`,
      details: { mover, original: mover.partPositionMm, trackLengthMm },
    });
    pos = trackLengthMm;
  }
  return { ...mover, partPositionMm: pos };
}

/**
 * Compute the would-be trackLength of a part WITHOUT building a full chain
 * (avoids dragging buildChain in here and creating a circular dep). The
 * merge step has already happened by the time we call this, so the
 * module-list lengths are summed via a static catalogue lookup.
 */
function trackLengthAfterMerge(modules: ReadonlyArray<ModuleEntry>): number {
  let sum = 0;
  for (const m of modules) {
    sum += MODULE_LENGTH[m.moduleType] ?? 0;
  }
  return sum;
}

const MODULE_LENGTH: Readonly<Record<ModuleType3D, number>> = Object.freeze({
  AT2000_0250: 250,
  AT2001_0250: 250,
  AT2002_0250: 250,
  AT2002_0249: 249,
  AT2002_0249_ZX2002_0001: 249,
  AT2000_0233: 233,
  AT2000_0249: 249,
  AT2100_0250: 250,
  AT2102_0250: 250,
  AT2020_0250: 250,
  AT2021_0250: 250,
  AT2025_0250: 250,
  AT2026_0250: 250,
  AT2040_0250: 250,
  AT2041_0250: 250,
  AT2042_0250: 250,
  AT2140_0250: 250,
  AT2050_0500: 250,
  AT2050_0501: 250,
  AT2050_0500_180: 500,
  AT2200_0500: 500,
  AT2202_0500: 500,
  ATH2000_0250: 250,
  ATH2001_0250: 250,
  ATH2002_0250: 250,
  ATH2020_0250: 250,
  ATH2040_0250: 250,
  ATH2041_0250: 250,
  ATH2042_0250: 250,
  ATH2050_0500: 250,
  ATH2050_0501: 250,
  ATH2050_0500_180: 500,
});

const STRUCTURAL_OMIT = new Set([
  'partPositionMm',
  'selected',
  'status',
]);

function structuralKey(config: XtsConfig): string {
  return JSON.stringify(config, (key, value) =>
    STRUCTURAL_OMIT.has(key) ? undefined : value,
  );
}

function cloneWithFreshPositions(
  cached: NormalizedXtsConfig,
  fresh: XtsConfig,
): NormalizedXtsConfig {
  const positionByMoverId = new Map<number, number>();
  const statusByMoverId = new Map<
    number,
    MoverConfig['status']
  >();
  const selectedByMoverId = new Map<number, boolean | undefined>();
  for (const xpu of fresh.processingUnits)
    for (const m of xpu.movers) {
      positionByMoverId.set(m.id, m.partPositionMm);
      statusByMoverId.set(m.id, m.status);
      selectedByMoverId.set(m.id, m.selected);
    }
  // Re-clamp + refresh frame-paced fields. Structural validation already
  // ran during the original cache-populate; we only revisit the
  // out-of-range warnings here.
  const warnings = cached.__warnings.filter(
    (w) => w.code !== 'mover-out-of-range',
  );
  const newXpus = cached.processingUnits.map((xpu) => {
    const trackLengths = new Map<number, number>();
    for (const p of xpu.parts) {
      let sum = 0;
      for (const m of p.modules)
        sum += MODULE_LENGTH[m.moduleType] ?? 0;
      trackLengths.set(p.globalNumber, sum);
    }
    return {
      ...xpu,
      movers: xpu.movers.map((m) => {
        const requested = positionByMoverId.get(m.id);
        const trackLength = trackLengths.get(m.partOid) ?? 0;
        let pos = requested ?? m.partPositionMm;
        if (!Number.isFinite(pos)) pos = 0;
        if (pos < 0) {
          warnings.push({
            code: 'mover-out-of-range',
            message: `Mover ${m.id} partPositionMm=${pos} clamped to 0.`,
            details: { mover: m, original: pos },
          });
          pos = 0;
        } else if (pos > trackLength) {
          warnings.push({
            code: 'mover-out-of-range',
            message: `Mover ${m.id} partPositionMm=${pos} clamped to trackLength=${trackLength}.`,
            details: { mover: m, original: pos, trackLength },
          });
          pos = trackLength;
        }
        const nextStatus = statusByMoverId.get(m.id);
        const nextSelected = selectedByMoverId.get(m.id);
        return {
          ...m,
          partPositionMm: pos,
          ...(nextStatus !== undefined ? { status: nextStatus } : {}),
          ...(nextSelected !== undefined ? { selected: nextSelected } : {}),
        };
      }),
    };
  });
  return { ...cached, processingUnits: newXpus, __warnings: warnings };
}

/**
 * Stateful normalizer with a single-slot structural cache. The key
 * intentionally excludes `mover.partPositionMm` so that frame-paced
 * position updates fall into the fast path: when the structure (modules,
 * parts, transforms, mover ids/partOids) hasn't changed between calls,
 * we return a clone of the cached result with only the mover positions
 * re-clamped against the current input.
 *
 * Construct your own instance when you need an isolated cache (e.g. in
 * tests, or when multiple viewers in the same JS realm would otherwise
 * thrash a shared single-slot cache). The standalone `normalizeXtsConfig`
 * function delegates to a process-wide singleton for backwards
 * compatibility.
 */
export class XtsConfigNormalizer {
  private _cache: {
    key: string;
    result: NormalizedXtsConfig;
  } | null = null;

  normalize(config: XtsConfig): NormalizedXtsConfig {
    const key = structuralKey(config);
    if (this._cache && this._cache.key === key) {
      return cloneWithFreshPositions(this._cache.result, config);
    }
    const result = normalizeXtsConfigImpl(config);
    this._cache = { key, result };
    return result;
  }

  clearCache(): void {
    this._cache = null;
  }
}

const _DEFAULT_NORMALIZER = new XtsConfigNormalizer();

export function normalizeXtsConfig(config: XtsConfig): NormalizedXtsConfig {
  return _DEFAULT_NORMALIZER.normalize(config);
}

/** Test hook — clears the default normalizer's cache between unit tests. */
export function _clearNormalizeCache(): void {
  _DEFAULT_NORMALIZER.clearCache();
}

function normalizeXtsConfigImpl(config: XtsConfig): NormalizedXtsConfig {
  const warnings: NormalizationWarning[] = [];
  const newXpus: ProcessingUnitConfig[] = [];

  // System-wide uniqueness checks for the host-supplied IDs:
  //   - ProcessingUnitConfig.objectId (the XPU identifier)
  //   - PartConfig.objectId           (referenced by MoverPositionEntry.partObjectId)
  //   - ModuleEntry.globalNumber      (referenced by setModuleStatuses(array) index)
  const seenXpuObjectIds = new Map<number, { xpuPosition: number }>();
  const seenPartObjectIds = new Map<
    number,
    { xpuObjectId: number; partPosition: number }
  >();
  const seenModuleGlobalNumbers = new Map<
    number,
    { xpuObjectId: number; partObjectId: number; moduleIndex: number }
  >();
  for (let xi = 0; xi < config.processingUnits.length; xi++) {
    const xpu = config.processingUnits[xi]!;
    const xoid = (xpu as { objectId?: unknown }).objectId;
    if (xoid === undefined || xoid === null) {
      warnings.push({
        code: 'missing-xpu-object-id',
        message: `XPU at position ${xi} is missing required field 'objectId'.`,
        details: { xpuPosition: xi },
      });
    } else if (!isValidUint32(xoid)) {
      warnings.push({
        code: 'invalid-xpu-object-id',
        message: `XPU at position ${xi} has invalid objectId=${String(xoid)} (must be a non-negative integer ≤ 2^32-1).`,
        details: { xpuPosition: xi, objectId: xoid },
      });
    } else {
      const prev = seenXpuObjectIds.get(xoid);
      if (prev) {
        warnings.push({
          code: 'duplicate-xpu-object-id',
          message: `Duplicate XPU.objectId=${xoid} (first at position ${prev.xpuPosition}, again at position ${xi}).`,
          details: { objectId: xoid, first: prev, second: { xpuPosition: xi } },
        });
      } else {
        seenXpuObjectIds.set(xoid, { xpuPosition: xi });
      }
    }
    const xpuObjectIdForMessages = isValidUint32(xoid) ? xoid : xi;
    for (let pi = 0; pi < xpu.parts.length; pi++) {
      const part = xpu.parts[pi]!;
      const oid = (part as { objectId?: unknown }).objectId;
      if (oid === undefined || oid === null) {
        warnings.push({
          code: 'missing-part-object-id',
          message: `Part #${part.globalNumber} (XPU ${xpuObjectIdForMessages}, position ${pi}) is missing required field 'objectId'.`,
          details: { xpuObjectId: xpuObjectIdForMessages, partPosition: pi },
        });
      } else if (!isValidUint32(oid)) {
        warnings.push({
          code: 'invalid-part-object-id',
          message: `Part #${part.globalNumber} (XPU ${xpuObjectIdForMessages}, position ${pi}) has invalid objectId=${String(oid)} (must be a non-negative integer ≤ 2^32-1).`,
          details: { xpuObjectId: xpuObjectIdForMessages, partPosition: pi, objectId: oid },
        });
      } else {
        const prev = seenPartObjectIds.get(oid);
        if (prev) {
          warnings.push({
            code: 'duplicate-part-object-id',
            message: `Duplicate Part.objectId=${oid} (first at XPU ${prev.xpuObjectId} part ${prev.partPosition}, again at XPU ${xpuObjectIdForMessages} part ${pi}).`,
            details: { objectId: oid, first: prev, second: { xpuObjectId: xpuObjectIdForMessages, partPosition: pi } },
          });
        } else {
          seenPartObjectIds.set(oid, { xpuObjectId: xpuObjectIdForMessages, partPosition: pi });
        }
      }
      const partObjectIdForMessages = isValidUint32(oid) ? oid : pi;
      for (let mi = 0; mi < part.modules.length; mi++) {
        const m = part.modules[mi]!;
        const gn = (m as { globalNumber?: unknown }).globalNumber;
        if (gn === undefined || gn === null) {
          warnings.push({
            code: 'missing-module-global-number',
            message: `Module '${m.moduleType}' (XPU ${xpuObjectIdForMessages}, part ${partObjectIdForMessages}, index ${mi}) is missing required field 'globalNumber'.`,
            details: { xpuObjectId: xpuObjectIdForMessages, partObjectId: partObjectIdForMessages, moduleIndex: mi, moduleType: m.moduleType },
          });
        } else if (!isValidUint32(gn)) {
          warnings.push({
            code: 'invalid-module-global-number',
            message: `Module '${m.moduleType}' (XPU ${xpuObjectIdForMessages}, part ${partObjectIdForMessages}, index ${mi}) has invalid globalNumber=${String(gn)} (must be a non-negative integer ≤ 2^32-1).`,
            details: { xpuObjectId: xpuObjectIdForMessages, partObjectId: partObjectIdForMessages, moduleIndex: mi, globalNumber: gn },
          });
        } else {
          const prev = seenModuleGlobalNumbers.get(gn);
          if (prev) {
            warnings.push({
              code: 'duplicate-module-global-number',
              message: `Duplicate ModuleEntry.globalNumber=${gn} (first at XPU ${prev.xpuObjectId} part ${prev.partObjectId} module ${prev.moduleIndex}, again at XPU ${xpuObjectIdForMessages} part ${partObjectIdForMessages} module ${mi}).`,
              details: { globalNumber: gn, first: prev, second: { xpuObjectId: xpuObjectIdForMessages, partObjectId: partObjectIdForMessages, moduleIndex: mi } },
            });
          } else {
            seenModuleGlobalNumbers.set(gn, { xpuObjectId: xpuObjectIdForMessages, partObjectId: partObjectIdForMessages, moduleIndex: mi });
          }
        }
      }
    }
  }

  for (const xpu of config.processingUnits) {
    if (!isKnownMoverType(xpu.moverType)) {
      throw new XtsViewerErrorException(
        'unknown-mover-type',
        `Unknown moverType "${xpu.moverType}" in XPU ${xpu.objectId}`,
        { xpuObjectId: xpu.objectId, moverType: xpu.moverType },
      );
    }
    const newParts: PartConfig[] = [];
    const partsByOid = new Map<number, PartConfig>();
    const partTrackLengths = new Map<number, number>();

    for (const part of xpu.parts) {
      // Merge halves first, then validate.
      const mergedModules = mergeHalves(part.modules);
      const newPart: PartConfig = { ...part, modules: mergedModules };
      warnings.push(...validatePart(newPart));
      newParts.push(newPart);
      partsByOid.set(newPart.globalNumber, newPart);
      partTrackLengths.set(
        newPart.globalNumber,
        trackLengthAfterMerge(mergedModules),
      );
    }

    const { movers, warnings: moverWarnings } = validateMovers(
      { ...xpu, parts: newParts },
      partsByOid,
      partTrackLengths,
    );
    warnings.push(...moverWarnings);

    newXpus.push({ ...xpu, parts: newParts, movers });
  }

  return {
    ...config,
    processingUnits: newXpus,
    __warnings: warnings,
  };
}
